import type { GameTimingMove } from '@wx-weiqi/contracts';

export interface TimingRange {
  readonly fromSequence: number;
  readonly toSequence: number;
}

export interface TimingSideSummary {
  readonly moveCount: number;
  readonly totalMs: number;
  readonly averageMs: number;
}

export interface TimingRangeSummary {
  readonly moves: readonly GameTimingMove[];
  readonly moveCount: number;
  readonly totalMs: number;
  readonly averageMs: number;
  readonly longestMove: GameTimingMove | null;
  readonly black: TimingSideSummary;
  readonly white: TimingSideSummary;
}

export interface TimingComparisonRow {
  readonly black: GameTimingMove | null;
  readonly white: GameTimingMove | null;
}

export type TimingMoveColorFilter = 'ALL' | 'BLACK' | 'WHITE';
export type TimingMoveDurationFilter =
  'ALL' | 'OVER_10_SECONDS' | 'OVER_30_SECONDS' | 'OVER_60_SECONDS';

const durationThresholds: Readonly<Record<Exclude<TimingMoveDurationFilter, 'ALL'>, number>> =
  Object.freeze({
    OVER_10_SECONDS: 10_000,
    OVER_30_SECONDS: 30_000,
    OVER_60_SECONDS: 60_000,
  });

function boundedInteger(value: number, minimum: number, maximum: number): number {
  if (!Number.isFinite(value)) return minimum;
  return Math.min(maximum, Math.max(minimum, Math.trunc(value)));
}

export function normalizeTimingRange(
  moveCount: number,
  requestedFrom: number,
  requestedTo: number,
): TimingRange {
  if (!Number.isSafeInteger(moveCount) || moveCount < 0) {
    throw new RangeError('Timing report move count is invalid');
  }
  if (moveCount === 0) return Object.freeze({ fromSequence: 0, toSequence: 0 });
  const fromSequence = boundedInteger(requestedFrom, 1, moveCount);
  const toSequence = boundedInteger(requestedTo, fromSequence, moveCount);
  return Object.freeze({ fromSequence, toSequence });
}

export function assertCompleteTimingMoves(
  moves: readonly GameTimingMove[],
  expectedMoveCount: number,
): readonly GameTimingMove[] {
  if (!Number.isSafeInteger(expectedMoveCount) || expectedMoveCount < 0) {
    throw new RangeError('Expected timing move count is invalid');
  }
  if (
    moves.length !== expectedMoveCount ||
    moves.some(
      (move, index) =>
        move.sequence !== index + 1 || move.elapsedMs === null || move.accumulatedMs === null,
    )
  ) {
    throw new Error('逐手用时与整盘报告不一致，请稍后重试。');
  }
  return Object.freeze([...moves]);
}

function sideSummary(moves: readonly GameTimingMove[]): TimingSideSummary {
  const totalMs = moves.reduce((total, move) => total + (move.elapsedMs ?? 0), 0);
  return Object.freeze({
    moveCount: moves.length,
    totalMs,
    averageMs: moves.length === 0 ? 0 : totalMs / moves.length,
  });
}

export function summarizeTimingRange(
  moves: readonly GameTimingMove[],
  range: TimingRange,
): TimingRangeSummary {
  const selected = moves.filter(
    (move) => move.sequence >= range.fromSequence && move.sequence <= range.toSequence,
  );
  const totalMs = selected.reduce((total, move) => total + (move.elapsedMs ?? 0), 0);
  const longestMove = [...selected].sort(
    (left, right) =>
      (right.elapsedMs ?? 0) - (left.elapsedMs ?? 0) || left.sequence - right.sequence,
  )[0];
  return Object.freeze({
    moves: Object.freeze(selected),
    moveCount: selected.length,
    totalMs,
    averageMs: selected.length === 0 ? 0 : totalMs / selected.length,
    longestMove: longestMove ?? null,
    black: sideSummary(selected.filter((move) => move.color === 'BLACK')),
    white: sideSummary(selected.filter((move) => move.color === 'WHITE')),
  });
}

export function buildTimingComparisonRows(
  moves: readonly GameTimingMove[],
): readonly TimingComparisonRow[] {
  const rows: TimingComparisonRow[] = [];
  let black: GameTimingMove | null = null;

  for (const move of moves) {
    if (move.color === 'BLACK') {
      if (black !== null) rows.push(Object.freeze({ black, white: null }));
      black = move;
      continue;
    }

    rows.push(Object.freeze({ black, white: move }));
    black = null;
  }

  if (black !== null) rows.push(Object.freeze({ black, white: null }));
  return Object.freeze(rows);
}

export function filterTimingMoves(
  moves: readonly GameTimingMove[],
  color: TimingMoveColorFilter,
  duration: TimingMoveDurationFilter,
): readonly GameTimingMove[] {
  const threshold = duration === 'ALL' ? null : durationThresholds[duration];
  return Object.freeze(
    moves.filter(
      (move) =>
        (color === 'ALL' || move.color === color) &&
        (threshold === null || (move.elapsedMs ?? 0) > threshold),
    ),
  );
}

export function formatPreciseDuration(milliseconds: number | null): string {
  if (milliseconds === null) return '—';
  const safe = Math.max(0, Math.trunc(milliseconds));
  const minutes = Math.floor(safe / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1_000);
  const fraction = safe % 1_000;
  return minutes > 0
    ? `${minutes}:${String(seconds).padStart(2, '0')}.${String(fraction).padStart(3, '0')}`
    : `${seconds}.${String(fraction).padStart(3, '0')} 秒`;
}
